import { useState, type FormEvent } from "react";
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import { signIn } from "../lib/firebase";
import { BrandMark } from "./BrandMark";

function getLoginError(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code.includes("invalid-credential")) return "Fel användarnamn eller lösenord.";
  if (code.includes("too-many-requests")) return "För många försök. Vänta en stund och försök igen.";
  if (code.includes("network-request-failed")) return "Ingen kontakt med tjänsten. Kontrollera anslutningen.";
  return "Inloggningen misslyckades. Försök igen.";
}

export function LoginScreen() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!username.trim() || !password) {
      setError("Fyll i både användarnamn och lösenord.");
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      await signIn(username, password);
    } catch (loginError) {
      setError(getLoginError(loginError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-story" aria-label="Kils OK klubbkläder">
        <div className="login-story__glow" />
        <BrandMark className="login-story__logo" variant="full" />
        <div className="login-story__content">
          <span className="eyebrow eyebrow--light">Kils OK · Klubbkläder</span>
          <h1>Beställ klubbkläder.</h1>
        </div>
      </section>

      <section className="login-panel">
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-form__heading">
            <span className="eyebrow">Välkommen tillbaka</span>
            <h2>Logga in</h2>
          </div>

          <label className="field">
            <span>Användarnamn eller e-post</span>
            <span className="field__control">
              <Mail size={19} aria-hidden="true" />
              <input
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Ditt användarnamn"
              />
            </span>
          </label>

          <label className="field">
            <span>Lösenord</span>
            <span className="field__control">
              <LockKeyhole size={19} aria-hidden="true" />
              <input
                autoComplete="current-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Ditt lösenord"
              />
              <button
                className="field__visibility"
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </span>
          </label>

          {error && <p className="form-error" role="alert">{error}</p>}

          <button className="primary-button primary-button--large" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Loggar in…" : "Logga in"}
            {!isSubmitting && <ArrowRight size={19} aria-hidden="true" />}
          </button>
        </form>
      </section>
    </main>
  );
}
